import React, { memo, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import styles from './VehicleGpsFiltersScreen.module.scss';
import Button from 'common/components/button/Button';
import FormItem from 'common/components/formItem/FormItem';
import Label from 'common/components/label/Label';
import { Col, Row } from 'react-flexbox-grid';
import { SelectValueLabel } from 'common/types/SelectValueLabel';
import Select from 'common/components/select/Select';
import { Controller, useForm } from 'react-hook-form';
import Input from 'common/components/input/Input';
import DriversService from 'api/drivers/DriversService';
import LocalsService from 'api/locals/LocalsService';
import VehicleSegmentsService from 'api/vehicleSegments/VehicleSegmentsService';
import Utils from 'common/services/Utils';

export interface Filters {
    registrationNumber?: string;
    driverId?: string;
    driverName?: string;
    ignition?: boolean;
    localIds?: string[];
    localNames?: string[];
    localOperator?: string;
    segmentId?: string;
    segmentName?: string;
}

type Props = {
    filters: Filters;
    onChange: (filters: Filters) => void;
    onFilter: (filters: Filters) => void;
}

const VehicleGpsFiltersScreen: React.FC<Props> = ({ filters, onChange, onFilter }: Props) => {
    const { t } = useTranslation();

    const form = useForm<Filters>({ shouldUnregister: false, shouldFocusError: true, defaultValues: filters });
    const { getValues, setValue, reset, watch, control } = form;

    const [driversOptions, setDriversOptions] = useState<SelectValueLabel[]>([]);
    const [localsOptions, setLocals] = useState<SelectValueLabel[]>([]);
    const [segmentsOptions, setSegments] = useState<SelectValueLabel[]>([]);
    const [selectIgnition, setSelectIgnition] = useState<boolean | undefined>();

    const getData = async () => {
        const [
            drivers,
            localsOptionsDB,
            segmentsOptionsDB
        ] = await Promise.all([
            DriversService.catalog(),
            LocalsService.catalog(),
            VehicleSegmentsService.catalog()
        ]);
        setDriversOptions(drivers);
        setLocals([{ value: '00000000-0000-0000-0000-000000000000', label: t('maintenances.list.none') as string }, ...Utils.sortAlphabetically(localsOptionsDB)]);
        setSegments(Utils.sortAlphabetically(segmentsOptionsDB));
    }

    useEffect(() => {
        if (form.getValues('ignition') != undefined) {
            setSelectIgnition(form.getValues('ignition'));
        }
        getData();
    }, []);

    const clearFilters = () => {
        reset({
            registrationNumber: undefined,
            driverId: undefined,
            ignition: undefined,
            localIds: [],
            localNames: [],
            localOperator: 'OR',
            segmentId: undefined,
            segmentName: undefined
        });
        onInputChange();
        onSubmit();
    }

    const onSubmit = () => {
        onFilter(getValues());
    }

    const onInputChange = () => {
        onChange(getValues());
    }

    const onSelectIgnition = (status: boolean) => {
        setSelectIgnition(selectIgnition == status ? undefined : status);
        setValue('ignition', selectIgnition == status ? undefined : status);
    }

    return (
        <form onSubmit={form.handleSubmit(onSubmit)}>
            <div className={styles.formContent}>
                <div>
                    <FormItem>
                        <Row>
                            <Col xs={6}>
                                <FormItem>
                                    <Label className={styles.bold}>{t('vehicle.registration')}</Label>
                                    <Input
                                        maxLength={255}
                                        name="registrationNumber"
                                        onKeyUp={e => {
                                            setValue('registrationNumber', e.currentTarget.value != '' ? e.currentTarget.value : null);
                                            onInputChange();
                                        }}
                                        placeholder={t('vehicle.registration')}
                                        autoComplete='off'
                                        defaultValue={watch('registrationNumber')} />
                                </FormItem>
                            </Col>
                            <Col xs={6}>
                                <FormItem>
                                    <Label className={styles.bold}>{t('vehicle.driver')}</Label>
                                    <Controller
                                        render={({ onChange: onSelectChange, value }) => {
                                            return (
                                                <Select
                                                    options={driversOptions}
                                                    isClearable
                                                    placeholder={t('vehicle.driver')}
                                                    onChange={(data: SelectValueLabel) => {
                                                        onSelectChange(data?.value ?? null);
                                                        setValue('driverName', data?.label ?? null);
                                                        onInputChange();
                                                    }}
                                                    value={driversOptions.find(x => x.value === value) ? { label: driversOptions.find(x => x.value === value)?.label ?? '', value: value } : null}
                                                />
                                            );
                                        }}
                                        control={control}
                                        name="driverId"
                                        defaultValue={getValues('driverId')} />
                                </FormItem>
                            </Col>
                        </Row>

                        <Row>
                            <Col xs={6}>
                                <FormItem>
                                    <Label className={styles.bold}>{t('vehicle.type')}</Label>
                                    <Controller
                                        render={({ onChange: onSelectChange, value }) => {
                                            return (
                                                <Select
                                                    options={segmentsOptions}
                                                    isClearable
                                                    placeholder={t('vehicle.type')}
                                                    onChange={(data: SelectValueLabel) => {
                                                        onSelectChange(data?.value ?? null);
                                                        setValue('segmentName', data?.label ?? null);
                                                        onInputChange();
                                                    }}
                                                    value={segmentsOptions.find(x => x.value === value) ? { label: segmentsOptions.find(x => x.value === value)?.label ?? '', value: value } : null}
                                                />
                                            );
                                        }}
                                        control={control}
                                        name="segmentId"
                                        defaultValue={getValues('segmentId')} />
                                </FormItem>
                            </Col>
                            <Col xs={6}>
                                <FormItem>
                                    <div className={styles.labelWithOperator}>
                                        <Label className={styles.bold}>{t('vehicle.local')}</Label>
                                        <div className={styles.operatorToggle}>
                                            <span title={t('common.filter_all_tooltip' as any) as string}>
                                                <Button
                                                    text={t('common.filter_all' as any) as string}
                                                    className={styles.firstChild}
                                                    preset={watch('localOperator') === 'AND' ? 'primary' : 'secondary'}
                                                    onClick={() => { setValue('localOperator', 'AND'); onInputChange(); }}
                                                    size={'small'}
                                                />
                                            </span>
                                            <span title={t('common.filter_any_tooltip' as any) as string}>
                                                <Button
                                                    text={t('common.filter_any' as any) as string}
                                                    className={styles.lastChild}
                                                    preset={watch('localOperator') !== 'AND' ? 'primary' : 'secondary'}
                                                    onClick={() => { setValue('localOperator', 'OR'); onInputChange(); }}
                                                    size={'small'}
                                                />
                                            </span>
                                        </div>
                                    </div>
                                    <Controller
                                        render={({ onChange: onSelectChange, value }) => (
                                            <Select
                                                options={localsOptions}
                                                isMulti
                                                isClearable
                                                placeholder={t('vehicle.local')}
                                                onChange={(data: SelectValueLabel[]) => {
                                                    onSelectChange(data?.map(d => d.value) ?? []);
                                                    setValue('localNames', data?.map(d => d.label) ?? []);
                                                    onInputChange();
                                                }}
                                                value={localsOptions.filter(x => ((value as string[]) ?? []).includes(x.value))}
                                            />
                                        )}
                                        control={control}
                                        name="localIds"
                                        defaultValue={getValues('localIds') || []}
                                    />
                                </FormItem>
                            </Col>
                        </Row>

                        <Row>
                            <Col xs={6}>
                                <FormItem>
                                    <Label className={styles.bold}>{t('common.state')}</Label>
                                    <div className={styles.useContractStatus}>
                                        <div onClick={() => onSelectIgnition(true)} style={{ width: '50%' }}>
                                            <div className={styles.labelContainer}>
                                                <div className={selectIgnition ? styles.selected : styles.notSelected}>
                                                    <span>{t('vehicle.ignition.ON')}</span>
                                                </div>
                                            </div>
                                        </div>
                                        <div onClick={() => onSelectIgnition(false)} style={{ width: '50%' }}>
                                            <div className={styles.labelContainer}>
                                                <div className={selectIgnition != undefined && !selectIgnition ? styles.selected : styles.notSelected}>
                                                    <span>{t('vehicle.ignition.OFF')}</span>
                                                </div>
                                            </div>
                                        </div>
                                    </div>
                                </FormItem>
                            </Col>
                        </Row>
                    </FormItem>
                </div>

                <div className={styles.buttonsFooter}>
                    <FormItem>
                        <Button
                            text={t('common.clear')}
                            size={'normal'}
                            preset={'secondary'}
                            onClick={clearFilters}
                            type='reset' />
                        <Button
                            type='submit'
                            text={t('common.apply')}
                            size={'normal'}
                        />
                    </FormItem>
                </div>
            </div>
        </form>
    );
}

export default memo(VehicleGpsFiltersScreen);